import { FileUploadConfig, FileType } from '@buffer-mono/shared-utils/uploads'

import { MAX_IMAGE_WORKSPACE_SOURCE_DIMENSION } from './imageWorkspaceSourceDescriptor'

export class HeicImagePreparationError extends Error {}

export const isHeicContentType = (contentType: string | null): boolean =>
  /^image\/hei[cf](?:-sequence)?(?:;|$)/i.test(contentType ?? '')

let conversionQueue: Promise<void> = Promise.resolve()

const convertHeicImage = async (
  blob: Blob,
  signal: AbortSignal,
  maxDimension = MAX_IMAGE_WORKSPACE_SOURCE_DIMENSION,
): Promise<Blob> => {
  signal.throwIfAborted()
  const maxSize = FileUploadConfig.IMAGE?.get(FileType.HEIC)?.maxSize
  if (maxSize === undefined || blob.size > maxSize) {
    throw new HeicImagePreparationError(
      'The HEIC image exceeds the upload size limit',
    )
  }

  const image = new Image()
  let objectUrl = URL.createObjectURL(blob)
  let converted: Blob | undefined
  try {
    image.src = objectUrl
    try {
      await image.decode()
    } catch {
      signal.throwIfAborted()
      const { default: heic2any } = await import('heic2any')
      signal.throwIfAborted()
      const result = await heic2any({
        blob,
        toType: 'image/jpeg',
        quality: 0.92,
      })
      signal.throwIfAborted()
      converted = Array.isArray(result) ? result[0] : result
      if (!converted)
        throw new HeicImagePreparationError('The HEIC image is empty')
      URL.revokeObjectURL(objectUrl)
      objectUrl = URL.createObjectURL(converted)
      image.src = objectUrl
      await image.decode()
    }
    signal.throwIfAborted()
    const scale = Math.min(
      1,
      maxDimension / image.naturalWidth,
      maxDimension / image.naturalHeight,
    )
    if (scale === 1 && converted) return converted

    const canvas = document.createElement('canvas')
    canvas.width = Math.max(1, Math.round(image.naturalWidth * scale))
    canvas.height = Math.max(1, Math.round(image.naturalHeight * scale))
    const context = canvas.getContext('2d')
    if (!context)
      throw new HeicImagePreparationError('Image preparation is unavailable')
    context.drawImage(image, 0, 0, canvas.width, canvas.height)
    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (prepared) => {
          canvas.width = 0
          canvas.height = 0
          if (signal.aborted) reject(signal.reason)
          else if (prepared) resolve(prepared)
          else
            reject(
              new HeicImagePreparationError(
                'The HEIC image could not be prepared',
              ),
            )
        },
        'image/jpeg',
        0.92,
      )
    })
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}

export const prepareHeicImage = (
  blob: Blob,
  signal: AbortSignal,
  maxDimension = MAX_IMAGE_WORKSPACE_SOURCE_DIMENSION,
): Promise<Blob> => {
  const conversion = conversionQueue.then(async () => {
    try {
      return await convertHeicImage(blob, signal, maxDimension)
    } catch (error) {
      signal.throwIfAborted()
      throw new HeicImagePreparationError(
        error instanceof Error
          ? error.message
          : 'The HEIC image could not be decoded',
      )
    }
  })
  conversionQueue = conversion.then(
    () => undefined,
    () => undefined,
  )
  return conversion
}
